/**
 * Time Frames list page.
 *
 * Customer-scoped (super_admin / reseller_owner need an entered customer
 * context to see the list). Columns: Ref, Name, Description (with a
 * hover popover showing the per-day or per-range schedule), Owner,
 * Active, row-menu actions. Delete is blocked with a friendly UI when
 * the frame is still referenced by IVR nodes.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeFrames, formatTimeFrameRef } from '@/api/timeFrames';
import type {
  TimeFrameRow, DaysOfWeekSlot, SpecificDateRange, TimeFrameInUseBody,
} from '@/api/timeFrames';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Plus, MoreHorizontal, Pencil, Trash2, Clock, Info, AlertTriangle,
} from 'lucide-react';
import { TimeFrameDialog } from './TimeFrameDialog';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

const DAY_LABELS: Record<string, string> = {
  sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday',
  thu: 'Thursday', fri: 'Friday', sat: 'Saturday',
};
const DAY_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function TimeFramesPage() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const { toast } = useToast();
  const canMutate = CAN_MUTATE.includes(user.role);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TimeFrameRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TimeFrameRow | null>(null);
  const [blockingError, setBlockingError] = useState<TimeFrameInUseBody | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['timeFrames', effectiveCustomerId],
    queryFn: () =>
      timeFrames.list({
        customer_id: effectiveCustomerId ?? undefined,
      }),
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const remove = useMutation({
    mutationFn: (idOrRef: string) => timeFrames.remove(idOrRef),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeFrames'] });
      toast({ title: 'Time frame deleted' });
      setDeleteTarget(null);
    },
    onError: (err: unknown) => {
      // apiFetch stashes the parsed JSON error body on `.details`. The 409
      // shape carries blocking_nodes; render that as the dedicated modal.
      const e = err as Error & {
        status?: number;
        code?: string;
        details?: unknown;
      };
      if (e.status === 409 && e.code === 'time_frame_in_use') {
        const body = e.details as TimeFrameInUseBody | undefined;
        if (body?.blocking_nodes) {
          setBlockingError(body);
          setDeleteTarget(null);
          return;
        }
      }
      toast({
        title: 'Could not delete',
        description: e.message || 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const columns = [
    {
      header: 'Ref',
      className: 'w-20',
      cell: (r: TimeFrameRow) => (
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
          {formatTimeFrameRef(r.ref_number)}
        </span>
      ),
    },
    {
      header: 'Name',
      cell: (r: TimeFrameRow) => (
        <Link
          to={`/time-frames/${formatTimeFrameRef(r.ref_number)}`}
          className="text-sm font-medium text-foreground hover:underline underline-offset-2"
        >
          {r.name}
        </Link>
      ),
    },
    {
      header: 'Description',
      cell: (r: TimeFrameRow) => <DescriptionCell row={r} />,
    },
    {
      header: 'Owner',
      cell: (r: TimeFrameRow) => (
        r.created_by_name
          ? <span className="text-xs text-slate-600 dark:text-slate-300">{r.created_by_name}</span>
          : <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
      ),
    },
    {
      header: 'Timezone',
      cell: (r: TimeFrameRow) => (
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
          {r.timezone}
        </span>
      ),
    },
    {
      header: 'Active',
      className: 'w-20',
      cell: (r: TimeFrameRow) => (
        r.active
          ? <Badge variant="default">Active</Badge>
          : <Badge variant="secondary">Inactive</Badge>
      ),
    },
    ...(canMutate ? [{
      header: '',
      className: 'w-12',
      cell: (r: TimeFrameRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditTarget(r)}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDeleteTarget(r)} className="text-red-600">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Time Frames"
        description="Reusable schedule definitions. Use them from scheduling IVR nodes (timer, day_of_week) instead of re-typing hours on every node."
        actions={canMutate
          ? <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Add a Timeframe</Button>
          : undefined}
      />

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-6">Loading…</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          empty={(
            <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
              <Clock className="h-10 w-10 mb-2 opacity-40" />
              <p className="text-sm">No time frames yet.</p>
              {canMutate && (
                <Button variant="link" onClick={() => setCreateOpen(true)} className="mt-1">
                  Add your first time frame
                </Button>
              )}
            </div>
          )}
        />
      )}

      {/* Create */}
      <TimeFrameDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {/* Edit */}
      <TimeFrameDialog
        open={!!editTarget}
        onOpenChange={(o) => { if (!o) setEditTarget(null); }}
        initial={editTarget ?? undefined}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this time frame?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              <strong>{deleteTarget?.name}</strong> will be soft-deleted. If any
              IVR nodes still reference it, we'll block the delete and tell
              you which ones first.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blocked-deletion explanation */}
      <Dialog open={!!blockingError} onOpenChange={(o) => { if (!o) setBlockingError(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Cannot delete — still in use
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p className="text-muted-foreground">
              This time frame is referenced by the following IVR nodes.
              Detach it from each node before deleting (open each node's
              editor or use the phone-admin "detach time frame" action).
            </p>
            <ul className="border rounded-md divide-y">
              {blockingError?.blocking_nodes.map((n) => (
                <li key={n.id} className="px-3 py-2 flex items-center gap-3">
                  <Badge variant="outline" className="font-mono text-xs">
                    IVR-{n.ref_number}
                  </Badge>
                  <span className="flex-1 font-medium">{n.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">{n.node_type}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setBlockingError(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Description column — short mode label + hover popover with the details
// ───────────────────────────────────────────────────────────────────────────

function DescriptionCell({ row }: { row: TimeFrameRow }) {
  const label = row.mode === 'always' ? 'Always'
    : row.mode === 'days_of_week' ? 'Days and Times'
    : 'Specific Dates';
  const hasDetails = row.mode !== 'always';
  return (
    <div className="inline-flex items-center gap-1.5">
      {hasDetails ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
            >
              {label}
              <Info className="h-3.5 w-3.5 opacity-70" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="p-0 w-auto min-w-[280px]">
            {row.mode === 'days_of_week' && <DaysOfWeekPopover row={row} />}
            {row.mode === 'specific_dates' && <SpecificDatesPopover row={row} />}
          </PopoverContent>
        </Popover>
      ) : (
        <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
      )}
    </div>
  );
}

function DaysOfWeekPopover({ row }: { row: TimeFrameRow }) {
  const slots = (row.rules.slots ?? []) as DaysOfWeekSlot[];
  // Group by day; keep only days that have slots.
  const byDay: Record<string, DaysOfWeekSlot[]> = {};
  for (const s of slots) {
    if (!byDay[s.day]) byDay[s.day] = [];
    byDay[s.day]!.push(s);
  }
  const activeDays = DAY_ORDER.filter((d) => byDay[d]?.length);
  if (activeDays.length === 0) {
    return (
      <div className="p-3 text-sm text-muted-foreground italic">
        No days configured.
      </div>
    );
  }
  return (
    <div className="text-sm">
      <div className="grid grid-cols-[auto_1fr] gap-x-4 px-3 py-2 border-b border-border font-medium">
        <div>Day</div>
        <div>Time</div>
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 px-3 py-2">
        {activeDays.map((d) => (
          <DaySlotRow key={d} day={d} slots={byDay[d]!} />
        ))}
      </div>
    </div>
  );
}

function DaySlotRow({ day, slots }: { day: string; slots: DaysOfWeekSlot[] }) {
  return (
    <>
      <div className="font-medium py-0.5">{DAY_LABELS[day]}</div>
      <div className="text-blue-600 dark:text-blue-400 font-mono text-xs py-0.5 self-center">
        {slots.map((s, i) => (
          <span key={i}>
            {i > 0 && ', '}
            {formatTime12(s.from)} to {formatTime12(s.to)}
          </span>
        ))}
      </div>
    </>
  );
}

function SpecificDatesPopover({ row }: { row: TimeFrameRow }) {
  const ranges = (row.rules.ranges ?? []) as SpecificDateRange[];
  if (ranges.length === 0) {
    return (
      <div className="p-3 text-sm text-muted-foreground italic">
        No ranges configured.
      </div>
    );
  }
  return (
    <div className="text-sm">
      <div className="grid grid-cols-2 gap-x-4 px-3 py-2 border-b border-border font-medium">
        <div>Begin</div>
        <div>End</div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 px-3 py-2">
        {ranges.map((r, i) => (
          <div key={i} className="contents">
            <div className="text-blue-600 dark:text-blue-400 font-mono text-xs py-0.5">
              {formatDateShort(r.from)}
            </div>
            <div className="text-blue-600 dark:text-blue-400 font-mono text-xs py-0.5">
              {formatDateShort(r.to)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** "09:00" → "9:00 am". 24-hour HH:MM in, friendly 12-hour out. */
function formatTime12(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const min = m[2]!;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${min} ${ampm}`;
}

/**
 * "2026-04-01T00:00" → "04/01/2026 12:00 am". Always shows the time so
 * the Begin column doesn't visually drop the clock alongside the End
 * column (Sam, 2026-05-17 — "today" frame was rendering Begin as just
 * the date because of an over-eager midnight-trim). Falls back to
 * date-only ONLY when the source has no time component at all
 * (legacy `YYYY-MM-DD` strings from before the dialog stopped using
 * date-only input).
 */
function formatDateShort(s: string): string {
  const t = s.replace(' ', 'T');
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(t);
  if (!m) return s;
  const date = `${m[2]}/${m[3]}/${m[1]}`;
  if (!m[4]) return date; // no time component at all — legacy date-only string
  const h = Number(m[4]);
  const min = m[5]!;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${date} ${h12}:${min} ${ampm}`;
}
